import api from "src/services/api";

const bufferToBase64Url = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const base64UrlToBuffer = (value: string) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

export const getWebAuthnPlatform = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("android")) return "android";
  if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod")) return "ios";
  return "web";
};

export const isMobileDevice = () => {
  return getWebAuthnPlatform() !== "web";
};

export const isWebAuthnSupported = () => {
  return typeof window !== "undefined"
    && typeof window.PublicKeyCredential !== "undefined"
    && window.isSecureContext;
};

export interface WebAuthnCredentialSummary {
  credential_id: string;
  plataforma?: string | null;
  user_agent?: string | null;
  creado_en?: string | null;
  actualizado_en?: string | null;
}

export const webauthnEstado = async () => {
  return api.get("webauthn/estado");
};

export const webauthnRegistrar = async () => {
  const plataforma = getWebAuthnPlatform();
  const user_agent = navigator.userAgent;
  const opcionesResp = await api.post("webauthn/registro/opciones", { plataforma, user_agent });
  const options = opcionesResp.data?.options || opcionesResp.data;

  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64UrlToBuffer(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map((cred: any) => ({
      ...cred,
      id: base64UrlToBuffer(cred.id),
    })),
  } as PublicKeyCredentialCreationOptions;

  const credential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential | null;
  if (!credential) throw new Error("No se pudo crear la credencial");

  const response = credential.response as AuthenticatorAttestationResponse;
  const transports = typeof response.getTransports === "function" ? response.getTransports() : undefined;

  const payload = {
    plataforma,
    user_agent,
    transports,
    credential: {
      id: credential.id,
      rawId: bufferToBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64Url(response.clientDataJSON),
        attestationObject: bufferToBase64Url(response.attestationObject),
      },
    },
  };

  return api.post("webauthn/registro/verificar", payload);
};

export const webauthnEliminar = async (credentialId?: string) => {
  return api.post("webauthn/eliminar", credentialId ? { credential_id: credentialId } : {});
};

// Indica si el dispositivo tiene un autenticador de plataforma (hardware seguro).
// Se usa para decidir si el operario puede enrolar la clave de dispositivo (Nivel B)
// o si se queda en el tier del UUID.
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  try {
    if (!isWebAuthnSupported()) return false;
    const pkc: any = window.PublicKeyCredential as any;
    if (typeof pkc?.isUserVerifyingPlatformAuthenticatorAvailable !== "function") return false;
    return await pkc.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

// Enrola la CLAVE DE DISPOSITIVO del operario (platform, sin verificación de usuario).
export const webauthnDispositivoRegistrar = async () => {
  const plataforma = getWebAuthnPlatform();
  const user_agent = navigator.userAgent;
  const opcionesResp = await api.post("webauthn/dispositivo/registro/opciones", {
    plataforma,
    user_agent,
  });
  const options = opcionesResp.data?.options || opcionesResp.data;

  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64UrlToBuffer(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map((cred: any) => ({
      ...cred,
      id: base64UrlToBuffer(cred.id),
    })),
  } as PublicKeyCredentialCreationOptions;

  const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!credential) throw new Error("No se pudo crear la clave de dispositivo");

  const response = credential.response as AuthenticatorAttestationResponse;
  const transports = typeof response.getTransports === "function" ? response.getTransports() : undefined;

  const payload = {
    plataforma,
    user_agent,
    transports,
    credential: {
      id: credential.id,
      rawId: bufferToBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64Url(response.clientDataJSON),
        attestationObject: bufferToBase64Url(response.attestationObject),
      },
    },
  };

  return api.post("webauthn/dispositivo/registro/verificar", payload);
};

// Login por CLAVE DE DISPOSITIVO (passwordless, sin verificación de usuario).
// Lanza error si el operario no tiene clave de dispositivo (el backend responde 404).
export const webauthnDispositivoLogin = async (cedula: number) => {
  const plataforma = getWebAuthnPlatform();
  const user_agent = navigator.userAgent;
  const opcionesResp = await api.post("webauthn/dispositivo/login/opciones", {
    emp_cedula: cedula,
    plataforma,
    user_agent,
  });
  const options = opcionesResp.data?.options || opcionesResp.data;

  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((cred: any) => ({
      ...cred,
      id: base64UrlToBuffer(cred.id),
    })),
  } as PublicKeyCredentialRequestOptions;

  const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!credential) throw new Error("No se pudo obtener la clave de dispositivo");

  const response = credential.response as AuthenticatorAssertionResponse;

  const payload = {
    emp_cedula: cedula,
    plataforma,
    user_agent,
    credential: {
      id: credential.id,
      rawId: bufferToBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64Url(response.clientDataJSON),
        authenticatorData: bufferToBase64Url(response.authenticatorData),
        signature: bufferToBase64Url(response.signature),
        userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
      },
    },
  };

  return api.post("webauthn/dispositivo/login/verificar", payload);
};

export const webauthnLogin = async (cedula: number) => {
  const plataforma = getWebAuthnPlatform();
  const user_agent = navigator.userAgent;
  const opcionesResp = await api.post("webauthn/login/opciones", {
    emp_cedula: cedula,
    plataforma,
    user_agent,
  });
  const options = opcionesResp.data?.options || opcionesResp.data;

  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((cred: any) => ({
      ...cred,
      id: base64UrlToBuffer(cred.id),
    })),
  } as PublicKeyCredentialRequestOptions;

  const credential = await navigator.credentials.get({ publicKey }) as PublicKeyCredential | null;
  if (!credential) throw new Error("No se pudo obtener la credencial");

  const response = credential.response as AuthenticatorAssertionResponse;

  const payload = {
    emp_cedula: cedula,
    plataforma,
    user_agent,
    credential: {
      id: credential.id,
      rawId: bufferToBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64Url(response.clientDataJSON),
        authenticatorData: bufferToBase64Url(response.authenticatorData),
        signature: bufferToBase64Url(response.signature),
        userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
      },
    },
  };

  return api.post("webauthn/login/verificar", payload);
};
